import { FormField } from '@common/types/api';

import {
  TextField,
  EmailField,
  PasswordField,
  NumberField,
  PhoneField,
  UrlField,
  TextareaField,
  SelectField,
  MultiSelectField,
  RadioField,
  CheckboxField,
  DateField,
  TimeField,
  DateTimeField,
  CpfField,
  CnpjField,
  CepField,
  StateField,
  CityField,
  AutocompleteField,
  FileField,
  ImageField,
  HiddenField,
  HeaderField,
  ParagraphField,
} from './fields';
import * as S from './styles';

/** Convert backend layout cols to frontend width */
const getFieldWidth = (field: FormField): 'full' | 'half' | 'third' => {
  // If width is already set, use it
  if (field.width) return field.width;

  // Convert from layout.cols if present
  const cols = (field as unknown as { layout?: { cols?: number } }).layout
    ?.cols;
  if (cols === 6) return 'half';
  if (cols === 4) return 'third';
  return 'full';
};

interface FieldRendererProps {
  field: FormField;
  disabled?: boolean;
}

const FieldRenderer = ({ field, disabled }: FieldRendererProps) => {
  const renderField = () => {
    switch (field.type) {
      case 'text':
      case 'name':
        return <TextField field={field} disabled={disabled} />;

      case 'email':
        return <EmailField field={field} disabled={disabled} />;

      case 'password':
        return <PasswordField field={field} disabled={disabled} />;

      case 'number':
        return <NumberField field={field} disabled={disabled} />;

      case 'tel':
      case 'phone':
        return <PhoneField field={field} disabled={disabled} />;

      case 'url':
        return <UrlField field={field} disabled={disabled} />;

      case 'textarea':
        return <TextareaField field={field} disabled={disabled} />;

      case 'select':
        return <SelectField field={field} disabled={disabled} />;

      case 'state':
        return <StateField field={field} disabled={disabled} />;

      case 'city':
        return <CityField field={field} disabled={disabled} />;

      case 'multiselect':
        return <MultiSelectField field={field} disabled={disabled} />;

      case 'radio':
        return <RadioField field={field} disabled={disabled} />;

      case 'checkbox':
      case 'toggle':
      case 'terms':
        return <CheckboxField field={field} disabled={disabled} />;

      case 'date':
      case 'birthdate':
        return <DateField field={field} disabled={disabled} />;

      case 'time':
        return <TimeField field={field} disabled={disabled} />;

      case 'datetime':
        return <DateTimeField field={field} disabled={disabled} />;

      case 'cpf':
        return <CpfField field={field} disabled={disabled} />;

      case 'cnpj':
      case 'cpf_cnpj':
        return <CnpjField field={field} disabled={disabled} />;

      case 'cep':
        return <CepField field={field} disabled={disabled} />;

      case 'autocomplete':
        if (field.depends_on) {
          return <CityField field={field} disabled={disabled} />;
        }
        return <AutocompleteField field={field} disabled={disabled} />;

      case 'file':
        return <FileField field={field} disabled={disabled} />;

      case 'image':
        return <ImageField field={field} disabled={disabled} />;

      case 'hidden':
        return <HiddenField field={field} disabled={disabled} />;

      case 'header':
      case 'heading':
        return <HeaderField field={field} disabled={disabled} />;

      case 'paragraph':
      case 'divider':
        return <ParagraphField field={field} disabled={disabled} />;

      default:
        return <TextField field={field} disabled={disabled} />;
    }
  };

  // Layout fields don't need label or wrapper
  if (
    field.type === 'header' ||
    field.type === 'paragraph' ||
    field.type === 'hidden'
  ) {
    return renderField();
  }

  return (
    <S.FieldWrapper $width={getFieldWidth(field)}>
      <S.FieldLabel $required={field.is_required}>{field.label}</S.FieldLabel>
      {renderField()}
      {field.help_text && <S.HelpText>{field.help_text}</S.HelpText>}
    </S.FieldWrapper>
  );
};

export default FieldRenderer;
